import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Chip, useTheme } from '@mui/material';
import { WalteroCard, WalteroTypography, WalteroButton } from '../waltero';
import { exportTableData } from '../../utils/csvExport';
import { usePerformanceData } from '../../context/PerformanceDataContext';
import { formatDate } from '../../utils/performanceFormatters';
import { 
  getAllDates, 
  getConfigBootReasonPivotData, 
  getConfigShadowUpdatesPivotData,
  getConfigImageUploadPivotData,
  getHeatmapColor
} from '../../utils/performanceDataProcessing';
import { hasOrganizationAccess } from '../../utils/auth';
import { ADMIN_ORGANIZATION_ID } from '../../config/api';

const DiagnosticConfigurationAnalysis = ({ 
  onBootReasonHover, 
  onShadowUpdateHover 
}) => {
  const theme = useTheme();
  const { performanceData, configurationData } = usePerformanceData(); 
  
  console.log('🔧 DiagnosticConfigurationAnalysis data sources:');
  console.log('🔧 performanceData:', performanceData?.length, performanceData);
  console.log('🔧 configurationData:', configurationData?.length, configurationData);
  console.log('🔧 Sample configurationData device:', configurationData?.[0]);
  
  // Check if shadow updates and image upload data exists in the performance data
  console.log('🔧 Shadow update stats in performanceData:', performanceData?.[0]?.shadowUpdateStats);
  console.log('🔧 Image upload stats in performanceData:', performanceData?.[0]?.imageUploadStats);

  // Process boot reason data (from original Lines 4018-4138)
  const { bootReasonPivotData, dates } = useMemo(() => {
    console.log('🔧 Processing boot reason data from performanceData:', performanceData);
    console.log('🔧 Processing boot reason data from configurationData:', configurationData);
    
    // Try configurationData first, then fallback to performanceData
    const dataSource = (configurationData && configurationData.length > 0) ? configurationData : performanceData;
    console.log('🔧 Using data source:', dataSource?.length > 0 ? 'configurationData' : 'performanceData');
    
    if (!dataSource || dataSource.length === 0) {
      console.log('🔧 No data available for boot reason analysis');
      return { bootReasonPivotData: [], dates: [] };
    }

    // Get dates from bootReasonStats.dailyStats
    const allDates = new Set();
    dataSource.forEach(device => {
      if (device.bootReasonStats?.dailyStats && Array.isArray(device.bootReasonStats.dailyStats)) {
        device.bootReasonStats.dailyStats.forEach(stat => {
          if (stat.date) allDates.add(stat.date);
        });
      }
    });

    const dateArray = Array.from(allDates).sort().reverse();
    console.log('🔧 Boot reason dates found:', dateArray);

    // Create boot reason pivot data
    const pivot = dataSource.map(device => {
      const devicePivot = {
        deviceId: device.deviceId,
        serialNumber: device.serialNumber,
        meterName: device.meterName
      };
      
      dateArray.forEach(date => {
        const bootStat = device.bootReasonStats?.dailyStats?.find(stat => stat.date === date);
        devicePivot[date] = bootStat ? {
          totalBoots: bootStat.totalBoots,
          problematicBoots: bootStat.problematicBoots,
          okBoots: bootStat.okBoots,
          bootReasons: {
            ...bootStat.problematicBootReasons,
            ...bootStat.okBootReasons
          }
        } : null;
      });
      
      return devicePivot;
    });

    console.log('🔧 Boot reason pivot:', pivot);
    return {
      bootReasonPivotData: pivot,
      dates: dateArray
    };
  }, [performanceData, configurationData]);

  // Process shadow updates data (from original Lines 4139-4261)
  const shadowUpdatesPivotData = useMemo(() => {
    console.log('🔧 Processing shadow updates data from performanceData:', performanceData);
    console.log('🔧 Processing shadow updates data from configurationData:', configurationData);
    
    const dataSource = (configurationData && configurationData.length > 0) ? configurationData : performanceData;
    
    if (!dataSource || dataSource.length === 0) {
      return [];
    }

    const pivot = dataSource.map(device => {
      const devicePivot = {
        deviceId: device.deviceId,
        serialNumber: device.serialNumber,
        meterName: device.meterName
      };
      
      dates.forEach(date => {
        const shadowStat = device.shadowUpdateStats?.dailyStats?.find(stat => stat.date === date);
        devicePivot[date] = shadowStat ? {
          hasUpdates: shadowStat.hasUpdates,
          updateCount: shadowStat.updateCount,
          updatedFields: shadowStat.updatedFields,
          fieldChanges: shadowStat.fieldChanges
        } : null;
      });
      
      return devicePivot;
    });

    console.log('🔧 Shadow updates pivot:', pivot);
    return pivot;
  }, [performanceData, configurationData, dates]);

  // Process image upload data 
  const imageUploadPivotData = useMemo(() => {
    console.log('🔧 Processing image upload data:', performanceData);
    
    if (!performanceData || performanceData.length === 0) {
      return [];
    }

    const pivot = performanceData.map(device => {
      const devicePivot = {
        deviceId: device.deviceId,
        serialNumber: device.serialNumber,
        meterName: device.meterName
      };
      
      dates.forEach(date => {
        const imageStat = device.imageUploadStats?.dailyStats?.find(stat => stat.date === date);
        devicePivot[date] = imageStat ? {
          imagesCreated: imageStat.imagesCreated,
          imagesUploaded: imageStat.imagesUploaded,
          totalImages: imageStat.totalImages
        } : null;
      });
      
      return devicePivot;
    });

    console.log('🔧 Image upload pivot:', pivot);
    return pivot;
  }, [performanceData, configurationData, dates]);

  // Only show if user has admin access
  if (!hasOrganizationAccess(ADMIN_ORGANIZATION_ID)) {
    return null;
  }

  const handleExportBootReason = () => {
    const exportData = bootReasonPivotData.map(device => {
      const row = {
        deviceId: device.deviceId,
        serialNumber: device.serialNumber,
        meterName: device.meterName
      };
      
      dates.forEach(date => {
        const dayData = device[date];
        row[`boots_${date}`] = dayData?.totalBoots || 0;
        row[`boot_reasons_${date}`] = dayData?.bootReasonStats ? 
          JSON.stringify(dayData.bootReasonStats) : 'N/A';
      });
      
      return row;
    });
    
    exportTableData('boot-reason-pivot', { pivotData: exportData, dates });
  };

  const handleExportShadowUpdates = () => {
    const exportData = shadowUpdatesPivotData.map(device => {
      const row = {
        deviceId: device.deviceId,
        serialNumber: device.serialNumber,
        meterName: device.meterName
      };
      
      dates.forEach(date => {
        const dayData = device[date];
        row[`shadow_updates_${date}`] = dayData?.shadowUpdateCount || 0;
      });
      
      return row;
    });
    
    exportTableData('shadow-updates-pivot', { pivotData: exportData, dates });
  };

  const handleExportImageUploads = () => {
    const exportData = imageUploadPivotData.map(device => {
      const row = {
        deviceId: device.deviceId,
        serialNumber: device.serialNumber,
        meterName: device.meterName
      };
      
      dates.forEach(date => {
        const dayData = device[date];
        row[`image_uploads_${date}`] = dayData?.imageUploads || 0;
        row[`upload_success_${date}`] = dayData?.imageUploadSuccess || 0;
        row[`upload_failures_${date}`] = dayData?.imageUploadFailures || 0;
      });
      
      return row;
    });
    
    exportTableData('image-upload-pivot', { pivotData: exportData, dates });
  };

  if (!performanceData || performanceData.length === 0) {
    return (
      <div className="diagnostic-container">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <WalteroTypography variant="h6">Diagnostic Configuration Analysis</WalteroTypography>
          <Chip 
            label="Admin only" 
            size="small" 
            sx={{ 
              bgcolor: '#D32F2F', 
              color: 'white', 
              fontSize: '0.7rem',
              fontWeight: 'bold'
            }} 
          />
        </div>
        <WalteroCard variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <WalteroTypography variant="h6" color="secondary">
            No diagnostic configuration data available
          </WalteroTypography>
        </WalteroCard>
      </div>
    );
  }

  return (
    <div className="diagnostic-container">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <WalteroTypography variant="h6">Diagnostic Configuration Analysis</WalteroTypography>
        <Chip 
          label="Admin only" 
          size="small" 
          sx={{ 
            bgcolor: '#D32F2F', 
            color: 'white', 
            fontSize: '0.7rem',
            fontWeight: 'bold'
          }} 
        />
      </div>

      {/* Boot Reason Analysis Table */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <WalteroTypography variant="h6">Boot Reason Analysis</WalteroTypography>
          <WalteroButton 
            onClick={handleExportBootReason}
            disabled={performanceData.length === 0}
            variant="outlined"
            size="small"
          >
            Export CSV
          </WalteroButton>
        </div>
        
        <WalteroTypography variant="body2" sx={{ color: '#666', mb: 2 }}>
          Shows problematic boots per device per day. Numbers show problematic/total boots. 
          Red borders indicate devices with unusual boot behavior that require attention.
        </WalteroTypography>
        
        <WalteroCard sx={{ overflowX: 'auto' }}>
          <table 
            className="performance-table"
            role="table"
            aria-label="Boot reason analysis for all devices"
          >
            <thead>
              <tr role="row">
                <th role="columnheader">Device</th>
                <th role="columnheader">Meter Name</th>
                {dates.map(date => (
                  <th key={date} role="columnheader">
                    {formatDate(date)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bootReasonPivotData.map((device) => (
                <tr key={device.deviceId} role="row">
                  <td role="cell">{device.serialNumber}</td>
                  <td role="cell">{device.meterName}</td>
                  {dates.map(date => {
                    const dayData = device[date];
                    const totalBoots = dayData?.totalBoots || 0;
                    const problematicBoots = dayData?.problematicBoots || 0;
                    const hasUnusualBoots = problematicBoots > 0;
                    
                    return (
                      <td 
                        key={date} 
                        role="cell"
                        style={{
                          textAlign: 'center',
                          cursor: dayData?.bootReasons ? 'pointer' : 'default',
                          backgroundColor: (() => {
                            if (totalBoots === 0) return 'transparent';
                            if (problematicBoots === 0) return '#e8f5e8'; // Light green for normal
                            if (problematicBoots >= 5) return '#ffcdd2'; // Light red for high problems
                            return '#fff3e0'; // Light orange for some problems
                          })(),
                          border: hasUnusualBoots ? '3px solid #f44336' : '1px solid #eee',
                          fontWeight: hasUnusualBoots ? 'bold' : 'normal',
                          color: hasUnusualBoots ? '#d32f2f' : '#333'
                        }}
                        onMouseEnter={(e) => {
                          if (dayData?.bootReasons && onBootReasonHover) {
                            onBootReasonHover({
                              ...dayData,
                              serialNumber: device.serialNumber,
                              date: date
                            }, { x: e.clientX, y: e.clientY });
                          }
                        }}
                        onMouseLeave={() => {
                          if (onBootReasonHover) {
                            onBootReasonHover(null, { x: 0, y: 0 });
                          }
                        }}
                        aria-label={`${device.serialNumber} boot analysis for ${formatDate(date)}: ${problematicBoots} problematic boots out of ${totalBoots} total`}
                      >
                        {totalBoots > 0 ? (
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>
                              {problematicBoots}
                            </div>
                            <div style={{ fontSize: '10px', color: '#666' }}>
                              /{totalBoots}
                            </div>
                          </div>
                        ) : 'N/A'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </WalteroCard>
      </div>

      {/* Shadow Configuration Updates Table */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <WalteroTypography variant="h6">Shadow Configuration Updates</WalteroTypography>
          <WalteroButton 
            onClick={handleExportShadowUpdates}
            disabled={performanceData.length === 0}
            variant="outlined"
            size="small"
          >
            Export CSV
          </WalteroButton>
        </div>
        
        <WalteroCard sx={{ overflowX: 'auto' }}>
          <table 
            className="performance-table"
            role="table"
            aria-label="Shadow configuration updates for all devices"
          >
            <thead>
              <tr role="row">
                <th role="columnheader">Device</th>
                <th role="columnheader">Meter Name</th>
                {dates.map(date => (
                  <th key={date} role="columnheader">
                    {formatDate(date)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shadowUpdatesPivotData.map((device) => (
                <tr key={device.deviceId} role="row">
                  <td role="cell">{device.serialNumber}</td>
                  <td role="cell">{device.meterName}</td>
                  {dates.map(date => {
                    const dayData = device[date];
                    const updateCount = dayData?.shadowUpdateCount || 0;
                    
                    return (
                      <td 
                        key={date} 
                        role="cell"
                        style={{
                          textAlign: 'center',
                          cursor: dayData?.shadowUpdates?.length > 0 ? 'pointer' : 'default',
                          backgroundColor: updateCount > 0 ? getHeatmapColor(updateCount, 1, 10) : 'transparent'
                        }}
                        onMouseEnter={(e) => {
                          if (dayData?.shadowUpdates?.length > 0 && onShadowUpdateHover) {
                            onShadowUpdateHover({
                              ...dayData,
                              serialNumber: device.serialNumber,
                              date: date
                            }, { x: e.clientX, y: e.clientY });
                          }
                        }}
                        onMouseLeave={() => {
                          if (onShadowUpdateHover) {
                            onShadowUpdateHover(null, { x: 0, y: 0 });
                          }
                        }}
                        aria-label={`${device.serialNumber} shadow updates for ${formatDate(date)}: ${updateCount} updates`}
                      >
                        {updateCount}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </WalteroCard>
      </div>

      {/* Image Upload Statistics Table */}
      <div style={{ marginBottom: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <WalteroTypography variant="h6">Image Upload Statistics</WalteroTypography>
          <WalteroButton 
            onClick={handleExportImageUploads}
            disabled={performanceData.length === 0}
            variant="outlined"
            size="small"
          >
            Export CSV
          </WalteroButton>
        </div>
        
        <WalteroCard sx={{ overflowX: 'auto' }}>
          <table 
            className="performance-table"
            role="table"
            aria-label="Image upload statistics for all devices"
          >
            <thead>
              <tr role="row">
                <th role="columnheader">Device</th>
                <th role="columnheader">Meter Name</th>
                {dates.map(date => (
                  <th key={date} role="columnheader">
                    {formatDate(date)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {imageUploadPivotData.map((device) => (
                <tr key={device.deviceId} role="row">
                  <td role="cell">{device.serialNumber}</td>
                  <td role="cell">{device.meterName}</td>
                  {dates.map(date => {
                    const dayData = device[date];
                    console.log(`🔧 Image upload cell for ${device.serialNumber} ${date}:`, dayData);
                    
                    const totalImages = dayData?.totalImages || 0;
                    const imagesCreated = dayData?.imagesCreated || 0;
                    const imagesUploaded = dayData?.imagesUploaded || 0;
                    
                    return (
                      <td 
                        key={date} 
                        role="cell"
                        style={{
                          textAlign: 'center',
                          fontSize: '11px'
                        }}
                        aria-label={`${device.serialNumber} image uploads for ${formatDate(date)}: ${imagesUploaded} uploaded, ${totalImages - imagesUploaded} failed`}
                      >
                        {totalImages > 0 ? (
                          <div>
                            <div style={{ color: '#4CAF50', fontWeight: 'bold' }}>✓ {imagesUploaded}</div>
                            {(totalImages - imagesUploaded) > 0 && (
                              <div style={{ color: '#F44336', fontWeight: 'bold' }}>✗ {totalImages - imagesUploaded}</div>
                            )}
                          </div>
                        ) : (
                          'N/A'
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </WalteroCard>
      </div>
    </div>
  );
};

DiagnosticConfigurationAnalysis.propTypes = {
  onBootReasonHover: PropTypes.func,
  onShadowUpdateHover: PropTypes.func
};

DiagnosticConfigurationAnalysis.defaultProps = {
  onBootReasonHover: () => {},
  onShadowUpdateHover: () => {}
};

export default DiagnosticConfigurationAnalysis;